import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  ExternalLink,
  FieldSelect,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import Icons from '../../../../components/Icons/Icons';
import { getUserTpe } from '../../../../util/genericHelpers';
import { TRAINING_SUIT_BRANDS, UserRole } from '../../../../util/enums';
import CartLink from '../CartLink/CartLink';
import { required } from '../../../../util/validators';

const CONTACT_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSdw1vESfZxPJV96eVggBwB7_OnLoVCZ-q3ystnDZ6EidhHR1g/viewform';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={classNames(css.loginLink, css.navLink)}>
      <FormattedMessage id="TopbarDesktop.signup" />
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.navLink}>
      <FormattedMessage id="TopbarDesktop.login" />
    </NamedLink>
  );
};

const NewListingLink = () => {
  return (
    <NamedLink name="NewListingPage" className={css.navLink}>
      <FormattedMessage id="TopbarDesktop.postNewProduct" />
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, currentUserHasListings }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      className={css.navLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <FormattedMessage id="TopbarDesktop.inbox" />
      {notificationDot}
    </NamedLink>
  );
};
const HomeLink = () => {
  return (
    <NamedLink className={css.navLink} name="LandingPage">
      <FormattedMessage id="TopbarDesktop.home" />
    </NamedLink>
  );
};
const JoinLink = () => {
  return (
    <NamedLink className={css.navLink} name="LandingPage">
      <FormattedMessage id="TopbarDesktop.join" />
    </NamedLink>
  );
};
const SoulSearchLink = () => {
  return (
    <NamedLink className={css.navLink} name="LandingPage">
      <FormattedMessage id="TopbarDesktop.soulSearch" />
    </NamedLink>
  );
};

const MarketplaceLink = () => {
  return (
    <NamedLink className={css.navLink} name="LandingPage">
      <FormattedMessage id="TopbarDesktop.marketplace" />
    </NamedLink>
  );
};

const CalendarLink = () => {
  return (
    <NamedLink className={css.navLink} name="LandingPage">
      <FormattedMessage id="TopbarDesktop.calendar" />
    </NamedLink>
  );
};

const FavLink = ({ count }) => {
  return (
    <NamedLink name="FavoritesPage" className={css.navLink}>
      <Icons name="unfavorite" />
      {count > 0 && <span className={css.cartBadge}>{count}</span>}
    </NamedLink>
  );
};
const ProfileMenu = ({ currentPage, currentUser, onLogout }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };
  const userType = getUserTpe(currentUser);
  const isPractitioner = userType === UserRole?.PRACTITIONER;
  const { adminToken } = currentUser?.attributes?.profile?.metadata || {};
  const isAdmin = adminToken && adminToken === process.env.REACT_APP_ADMIN_USER_TOKEN;

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <FormattedMessage id="TopbarDesktop.yourListingsLink" />
          </NamedLink>
        </MenuItem>
        {isPractitioner ? (
          <MenuItem key="EditPractitionerPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('EditPractitionerPage'))}
              name="EditPractitionerPage"
            >
              <FormattedMessage id="TopbarDesktop.practitionerSettingsLink" />
            </NamedLink>
          </MenuItem>
        ) : (
          <MenuItem key="ProfileSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>
          </MenuItem>
        )}
        { isAdmin ? (
          <MenuItem key="SalesSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('SalesSettingsPage'))}
              name="SalesSettingsPage"
            >
              <FormattedMessage id="TopbarDesktop.salesLink" />
            </NamedLink>
          </MenuItem>
        ) : null }
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {boolean} props.currentUserHasListings
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    isHeaderWithoutBg,
    initialSearchFormValues = {},
  } = props;
  const [mounted, setMounted] = useState(false);

  const favoritesTotal = currentUser?.attributes?.profile?.publicData?.favorites || [];
  const favItemsCount = favoritesTotal.length;

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(
    rootClassName || css.root,
    className,
    isHeaderWithoutBg ? css.topbarDesktopWithoutBg : null
  );

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu currentPage={currentPage} currentUser={currentUser} onLogout={onLogout} />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;
  const newListingLinkMaybe = !isAuthenticatedOrJustHydrated ? null : <NewListingLink />;
  const cartLinkMaybe = isAuthenticatedOrJustHydrated ? <CartLink /> : null;
  const favLinkMaybe = isAuthenticatedOrJustHydrated ? <FavLink count={favItemsCount} /> : null;

const customTopbarLinks = () => {
  const howItWorksMenu = (
    <Menu>
      <MenuLabel className={css.navLink} isOpenClassName={css.navLink}>
        <FormattedMessage id="TopbarDesktop.howItWorks" />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="HowItWorksForBuyers">
          <NamedLink className={css.navMenuLink} name="CMSPage" params={{ pageId: 'how_it_works'}} to={{ hash: 'buyers-flow'}}>
            <FormattedMessage id="TopbarDesktop.howItWorksBuyersFlow" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="HowItWorksForSellers">
          <NamedLink className={css.navMenuLink} name="CMSPage" params={{ pageId: 'how_it_works'}} to={{ hash: 'sellers-flow'}}>
            <FormattedMessage id="TopbarDesktop.howItWorksSellersFlow" />
          </NamedLink>
        </MenuItem>
      </MenuContent>
    </Menu>
  );

  const helpMenu = (
    <Menu>
      <MenuLabel className={css.navLink} isOpenClassName={css.navLink}>
        <FormattedMessage id="TopbarDesktop.help" />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="FaqPage">
          <NamedLink className={css.navMenuLink} name="FAQPage">
            <FormattedMessage id="TopbarDesktop.faq" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ContactUsForm">
          <ExternalLink className={css.navMenuLink} href={CONTACT_FORM_URL}>
            <FormattedMessage id="TopbarDesktop.contactForm" />
          </ExternalLink>
        </MenuItem>
      </MenuContent>
    </Menu>
  );

  return (
    <>
      <NamedLink className={css.navLink} name="LandingPage">
        <FormattedMessage id="TopbarDesktop.home" />
      </NamedLink>

      <NamedLink className={css.navLink} name="SearchPage">
        <FormattedMessage id="TopbarDesktop.shop" />
      </NamedLink>

      <NamedLink className={css.navLink} name="CMSPage" params={{ pageId: 'team_branded_gear' }}>
        <FormattedMessage id="TopbarDesktop.teamGear" />
      </NamedLink>

      {howItWorksMenu}

      <NamedLink className={css.navLink} name="CMSPage" params={{ pageId: 'blog' }}>
        <FormattedMessage id="TopbarDesktop.blog" />
      </NamedLink>

      <NamedLink className={css.navLink} name="AboutPage">
        <FormattedMessage id="TopbarDesktop.about" />
      </NamedLink>

      {helpMenu}

      <NamedLink className={css.navLink} name="CMSPage" params={{ pageId: 'contact_us' }}>
        <FormattedMessage id="TopbarDesktop.contact" />
      </NamedLink>

      <NamedLink className={css.navLink} name="CMSPage" params={{ pageId: 'seller_checklist' }}>
        <FormattedMessage id="TopbarDesktop.createListing" />
      </NamedLink>
    </>
  );
};

  return (
    <nav className={classes}>
      <div className={css.contentWidth}>
        <div className={css.leftLinks}>
          <LinkedLogo
            className={css.logoLink}
            isHeaderWithoutBg={isHeaderWithoutBg}
            layout="desktop"
            alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
            linkToExternalSite={config?.topbar?.logoLink}
          />{' '}
          {customTopbarLinks()}
        </div>
        <div className={css.rightLinks}>
          <TopbarSearchForm
            desktopInputRoot={css.topbarSearchWithLeftPadding}
            onSubmit={onSearchSubmit}
            initialValues={initialSearchFormValues}
            appConfig={config}
          />
          <CustomLinksMenu
            currentPage={currentPage}
            customLinks={customLinks}
            intl={intl}
            hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
          />
          {/* {newListingLinkMaybe} */}
          {inboxLinkMaybe}
          {favLinkMaybe}
          {cartLinkMaybe}
          {profileMenuMaybe}
          {loginLinkMaybe}
          {signupLinkMaybe}
        </div>
      </div>
    </nav>
  );
};

export default TopbarDesktop;
